import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAdd,
  MdClose,
  MdOutlineInfo,
  MdOutlineInventory2,
} from "react-icons/md";
import Button from "shared/components/Button";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";

export default function ProductOptionsSection({
  config,
  availableOptions = [],
  selectedProducts = [],
  onAddProduct,
  onDeleteProduct,
}) {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.COMPANY);
  const PRODUCT_OPTIONS = t("PRODUCT_OPTIONS_SECTION", { returnObjects: true });

  const [selectedBaseProductId, setSelectedBaseProductId] = useState(null);

  function handleAddProduct() {
    if (!selectedBaseProductId) return;
    onAddProduct?.(selectedBaseProductId);
    setSelectedBaseProductId(null);
  }

  function handleDeleteProduct(productId) {
    if (!productId) return;
    onDeleteProduct?.(productId);
  }

  const availableOptionsFiltered = availableOptions.filter(
    (option) => !selectedProducts.some((p) => p.id === option.value),
  );

  return (
    <div className="company__space-y-8">
      <div className="company__info-box company__p-4 company__flex company__gap-3">
        <MdOutlineInfo
          className="company__w-5 company__h-5 company__text-blue-600 company__flex-shrink-0"
          style={{ marginTop: "0.125rem" }}
        />
        <div className="company__text-sm company__text-blue-900">
          <p className="company__font-medium company__mb-1">
            {PRODUCT_OPTIONS.INFO_BOX.TITLE}
          </p>
          <p>
            {PRODUCT_OPTIONS.INFO_BOX.DESCRIPTION}{" "}
            <span className="company__font-semibold">
              {PRODUCT_OPTIONS.INFO_BOX.CATALOG_LINK}
            </span>
            .
          </p>
        </div>
      </div>

      <div>
        <h3 className="company__text-lg company__font-semibold company__text-slate-900 company__mb-4">
          {PRODUCT_OPTIONS.BASE_PRODUCTS.TITLE}
        </h3>
        <p className="company__text-sm company__text-slate-600 company__mb-4">
          {PRODUCT_OPTIONS.BASE_PRODUCTS.DESCRIPTION}
        </p>

        <div className="company__flex company__gap-2 company__mb-6">
          <div className="company__flex-1">
            <Select
              showSearch
              className="company__w-full"
              options={availableOptionsFiltered}
              value={selectedBaseProductId}
              onChange={(value) => {
                setSelectedBaseProductId(value);
              }}
              placeholder={PRODUCT_OPTIONS.BASE_PRODUCTS.SELECT_PLACEHOLDER}
            />
          </div>
          <Button
            type="primary"
            onClick={handleAddProduct}
            disabled={!selectedBaseProductId}
          >
            <MdAdd className="company__w-5 company__h-5" />
            {PRODUCT_OPTIONS.BASE_PRODUCTS.ADD_BUTTON}
          </Button>
        </div>

        {selectedProducts.length === 0 ? (
          <div className="company__p-8 company__bg-slate-50 company__border company__border-slate-200 company__rounded-lg company__text-center">
            <MdOutlineInventory2 className="company__w-12 company__h-12 company__text-slate-400 company__mx-auto company__mb-3" />
            <p className="company__text-slate-600">
              {PRODUCT_OPTIONS.EMPTY_STATE.NO_PRODUCT}
            </p>
            <p className="company__text-sm company__text-slate-500 company__mt-1">
              {PRODUCT_OPTIONS.EMPTY_STATE.CTA}
            </p>
          </div>
        ) : (
          <div className="company__space-y-6">
            {selectedProducts.map((product) => (
              <div
                key={product.id}
                className="company__border company__border-slate-200 company__rounded-lg"
              >
                <div className="company__p-4 company__bg-blue-50 company__border-b company__border-blue-200">
                  <div className="company__flex company__items-start company__justify-between">
                    <div className="company__flex-1">
                      <div className="company__flex company__items-center company__gap-2 company__mb-1">
                        <MdOutlineInventory2 className="company__w-5 company__h-5 company__text-blue-600" />
                        <div className="company__font-semibold company__text-slate-900">
                          {product.name}
                        </div>
                      </div>
                      {product.description && (
                        <div className="company__text-sm company__text-slate-600 company__mb-2">
                          {product.description}
                        </div>
                      )}
                    </div>
                    <MdClose
                      onClick={() => handleDeleteProduct(product.id)}
                      color={COLORS.C_DANGER}
                      className="company__cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {availableOptions.length === 0 && (
          <div className="company__mt-4 company__p-4 company__bg-slate-50 company__border company__border-slate-200 company__rounded-lg company__text-center company__text-slate-600">
            {PRODUCT_OPTIONS.EMPTY_STATE.NO_CATALOG}
          </div>
        )}
      </div>
    </div>
  );
}
